import React, { useState, useEffect } from "react";
import { intervalLabel } from "../../utils/habitInterval";
// ListItem (calendar list-view cluster). The detail panel opened from this
// card's chevron is the shared ItemDetailPanel (see ./ItemDetailPanel.jsx).
import { Check, RefreshCw, Flag } from "lucide-react";
import { themeTone } from "../../theme/colorEngine";
import { clockInInk } from "../../theme/clockInInk";
import { now, todayStr } from "../../utils/dateHelpers";
import { listDragState } from "../../utils/dragState";
import { taskDueLabel } from "../../utils/taskDueLabel";
import { HabitCheckbox } from "../../components/HabitCheckbox";
import { isGoogleTaskEvent } from "../../google/googleTask";
import { isEventPassed } from "../../utils/eventStatus";
import { PRIORITY_COLORS, DEFAULT_TASK_COLOR, DEFAULT_EVENT_COLOR } from "../../constants/palettes";

function ListPriorityFlag({ priority }) {
  if (!priority) return null;
  const c = PRIORITY_COLORS[priority];
  if (!c) return null;
  // Just the coloured flag — the fill colour is what signals the urgency
  // level, so no text label or pill background is shown on the card.
  return (
    <span title={`Priority: ${priority}`} style={{
      display: "inline-flex", alignItems: "center", flexShrink: 0, color: c,
    }}>
      <Flag size={13} fill={c} strokeWidth={2.5} />
    </span>
  );
}

// A recurring series is shown as a SINGLE list row (its nearest occurrence),
// so this badge signals that it repeats instead of the list showing one card
// per occurrence. Reads the new frequencyType, falling back to the legacy
// event `repeat` field.
function repeatLabel(item) {
  const ft = item.frequencyType;
  if (ft && ft !== "once") {
    if (ft === "daily") return "Daily";
    if (ft === "specific" || ft === "count") return "Weekly";
    if (ft === "monthly") return "Monthly";
    if (ft === "yearly") return "Yearly";
    if (ft === "interval") return intervalLabel(item);
  }
  if (item.repeat && item.repeat !== "None") return item.repeat;
  return null;
}

// Ids of cards that have already played their mount-in animation at least
// once. Module-level (not component state) so it survives the List view
// remounting — which happens every time the Planner tab is switched away
// from and back to, since it's inside App.jsx's tab-keyed `.clarity-tab-content`
// — instead of resetting and replaying the slide-in for every card whenever
// the user just revisits the tab. Only cleared by a full page reload, which
// is fine: that's a fresh session where "first render" and "first ever
// appearance" are the same thing anyway.
const seenListItemIds = new Set();

function ListRepeatBadge({ item }) {
  const label = repeatLabel(item);
  if (!label) return null;
  return (
    <span title={`Repeats ${label.toLowerCase()}`} style={{
      display: "inline-flex", alignItems: "center", gap: 3,
      fontSize: 9, fontWeight: 700, letterSpacing: "0.04em",
      color: "var(--c-text-soft)", flexShrink: 0,
    }}>
      <RefreshCw size={10} /> {label}
    </span>
  );
}

export const ListItem = React.memo(function ListItem({ x, categories,
  setListDetailItem, setListDetailType, setListDetailDate,
  setCalEvents, setCalReminders, setTasks,
  isDesktop = false, calSubTab = "week",
  selectMode = false, selected = false, onToggleSelect,
}) {
  const { item, type, date } = x;
  const ink = clockInInk();

  const fmtT = (t) => t ? new Date(`2000-01-01T${t}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : null;
  const timeStr = item.time
    ? (item.endTime ? `${fmtT(item.time)} – ${fmtT(item.endTime)}` : fmtT(item.time))
    : null;
  const wasDragged = React.useRef(false); // blocks the synthetic click that fires after dragend
  const cardTouchStart = React.useRef(null); // { x, y }: tells a tap on the card from a scroll passing over it

  // Whether THIS card should play its mount-in animation — true only the
  // very first time this item/type pair is ever rendered (a genuinely new
  // card, or the first paint after a fresh page load), false on every later
  // remount of the same id (e.g. switching tabs away from and back to
  // Planner), so re-entering List view doesn't replay a slide for every
  // card already sitting there.
  const shouldAnimateInRef = React.useRef(null);
  if (shouldAnimateInRef.current === null) {
    const entryKey = `${type}:${item.id}`;
    shouldAnimateInRef.current = !seenListItemIds.has(entryKey);
    seenListItemIds.add(entryKey);
  }

  const openDetail = () => {
    setListDetailItem(item);
    setListDetailType(type);
    setListDetailDate(date);
  };

  // Tasks: the checkbox is the ONLY way to mark a task done/not-done — no tap or
  // swipe on the card body triggers this anymore.
  // `pendingCheck` makes the checkbox fill/checkmark flip in the same frame as
  // its bounce+petal-burst (which HabitCheckbox fires immediately on click,
  // before calling onChange) — the actual `item.done` write is still delayed
  // below so the Pending→Finished bucket move keeps its burst-then-slide beat.
  // Without this the box stayed empty for the whole 280ms+320ms delay, so the
  // burst read as detached from the checkbox instead of caused by it (unlike
  // the Habits checkbox, which flips its backing state immediately on tap).
  const [pendingCheck, setPendingCheck] = useState(false);
  // A Google Task synced in from Google Calendar is stored as an event but listed
  // and ticked like a task; its done mark is the event's `passed` (the same one
  // the details panel's Mark as done sets). See src/google/googleTask.js.
  const gTask = type === "event" && isGoogleTaskEvent(item);
  const asTask = type === "task" || gTask;
  const isDone = asTask ? (!!(gTask ? item.passed : item.done) || pendingCheck) : false;
  const toggleTaskDone = () => {
    if (gTask) {
      setCalEvents(p => p.map(e => e.id === item.id ? { ...e, passed: !isDone } : e));
      return;
    }
    setTasks(p => p.map(t => t.id === item.id
      ? { ...t, done: !isDone, completedAt: !isDone ? (t.completedAt || new Date().toISOString()) : t.completedAt }
      : t));
  };

  // Completing a task: let the checkbox's petal-burst read on its own for a beat,
  // THEN slide the whole card out, THEN flip `done` — so the item's exit from
  // this list (it moves to the Finished bucket) reads as burst-then-slide instead
  // of the card sliding away while the petals are still exploding over it.
  // Un-completing has no exit to animate, so it applies immediately.
  const [isExiting, setIsExiting] = useState(false);
  const exitTimerRef = React.useRef(null);
  const slideTimerRef = React.useRef(null);
  useEffect(() => () => {
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    if (slideTimerRef.current) clearTimeout(slideTimerRef.current);
  }, []);
  const handleTaskCheckboxChange = () => {
    if (isDone) {
      // Undo — also cancels a still-pending completion (tapped again before
      // the exit-slide fired) so it doesn't slide away out from under the tap.
      if (exitTimerRef.current) { clearTimeout(exitTimerRef.current); exitTimerRef.current = null; }
      if (slideTimerRef.current) { clearTimeout(slideTimerRef.current); slideTimerRef.current = null; }
      setPendingCheck(false);
      setIsExiting(false);
      toggleTaskDone();
      return;
    }
    setPendingCheck(true);
    exitTimerRef.current = setTimeout(() => {
      setIsExiting(true);
      slideTimerRef.current = setTimeout(() => { toggleTaskDone(); setPendingCheck(false); }, 320);
    }, 280);
  };

  // Events/reminders: passed once their date + time has gone by. An event can
  // also be marked passed, or kept in Upcoming, by hand (utils/eventStatus.js).
  const isPassed = (() => {
    if (asTask) return false;
    if (type === "event") return isEventPassed(item, date, now());
    const today_ = todayStr();
    if (date < today_) return true;
    if (date > today_) return false;
    if (!item.time) return false;
    const [hh, mm] = item.time.split(":").map(Number);
    const n = now();
    return (hh * 60 + mm) < (n.getHours() * 60 + n.getMinutes());
  })();

  return (
    <div style={{ position: "relative", borderRadius: 14, overflow: "hidden" }}>
      <div
        draggable={isDesktop && !isDone && !isPassed && !selectMode && calSubTab !== "month" && calSubTab !== "year"}
        onDragStart={e => {
          wasDragged.current = true;
          const itemColor = (() => {
            if (type === "task") {
              if (item.color) return item.color;
              const cat = item.goalId ? categories.find(c => c.id === item.goalId) : null;
              return cat?.color || DEFAULT_TASK_COLOR;
            }
            if (type === "reminder") {
              const cat = item.categoryId ? categories.find(c => c.id === item.categoryId) : null;
              return cat?.color || themeTone("#5bbfb5");
            }
            const cat = (item.categoryId || item.goalId) ? categories.find(c => c.id === (item.categoryId || item.goalId)) : null;
            return cat?.color || DEFAULT_EVENT_COLOR;
          })();
          const payload = { id: item.id, type, date, color: itemColor, title: item.title || item.name || "Untitled" };
          listDragState.current = payload;
          e.dataTransfer.setData("clarity-list-item", JSON.stringify(payload));
          e.dataTransfer.effectAllowed = "move";
          // Suppress the browser's default drag image (a snapshot of the card that
          // follows the cursor) — we only want the in-grid block preview to show.
          const ghost = document.createElement("div");
          ghost.style.cssText = "position:absolute;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;";
          document.body.appendChild(ghost);
          e.dataTransfer.setDragImage(ghost, 0, 0);
          setTimeout(() => { try { document.body.removeChild(ghost); } catch (_) {} }, 0);
        }}
        onDragEnd={() => { listDragState.current = null; setTimeout(() => { wasDragged.current = false; }, 300); }}
        // list-glass: on gradient backgrounds the card is the habit cards' see-through
        // glass instead of white (Jade, 2026-10-24; App.jsx GLASS_TARGETS). Not while
        // picked in select mode, so the picked fill still shows.
        className={selected ? undefined : "list-glass"}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "10px 14px",
          background: selected ? "var(--c-bg-active)" : "var(--c-bg-card)",
          borderRadius: 14,
          border: selected ? "1.5px solid var(--c-primary-deep)" : "1.5px solid var(--c-border-light)",
          boxShadow: "0 1px 4px var(--c-shadow)",
          position: "relative",
          opacity: isExiting ? 0 : (isDone || isPassed) && !selectMode ? 0.72 : 1,
          transform: isExiting ? "translateX(110%) scale(0.94)" : "translateX(0) scale(1)",
          transition: "transform 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.34s ease",
          // Same entrance used for a newly-added habit row (App.jsx's shared
          // rowIn keyframes) — this card had none, so a new task/event just
          // popped into the list instantly instead of sliding in like a habit
          // does. Only ever plays once, on this DOM node's first mount (a new
          // card, or an existing one appearing here for the first time after
          // switching sub-tabs). Fill is "backwards", NOT "both": a finished
          // animation with forwards fill keeps overriding the inline transform and
          // opacity, so a card's first completion had no exit slide at all (it just
          // vanished; after an uncheck the remounted card had no animation and slid
          // fine. Jade, 2026-10-16). Also dropped while exiting, to be safe.
          animation: shouldAnimateInRef.current && !isExiting ? "rowIn 0.24s cubic-bezier(0.22,1,0.36,1) backwards" : "none",
          userSelect: "none",
          // A tap anywhere on the card opens its details (Jade, 2026-11-02; it had a
          // ">" button for that). The task checkbox and the drag handle keep their
          // own jobs (data-no-detail). In select mode a tap picks the card.
          cursor: "pointer",
        }}
        onClick={e => {
          if (selectMode) { onToggleSelect && onToggleSelect(); return; }
          if (wasDragged.current) { wasDragged.current = false; return; }
          if (e.target.closest && e.target.closest("[data-no-detail]")) return;
          openDetail();
        }}
        // Touch: a lift within a few px of where the finger went down is a tap; one
        // that moved was a scroll / swipe passing over the card. Handled here (and the
        // click that would follow is cancelled) so the phone's tap is reliable.
        onTouchStart={e => {
          if (selectMode || (e.target.closest && e.target.closest("[data-no-detail]"))) { cardTouchStart.current = null; return; }
          const t = e.touches[0];
          cardTouchStart.current = { x: t.clientX, y: t.clientY };
        }}
        onTouchEnd={e => {
          const start = cardTouchStart.current;
          cardTouchStart.current = null;
          if (!start || selectMode) return;
          const t = e.changedTouches[0];
          if (t && (Math.abs(t.clientX - start.x) > 8 || Math.abs(t.clientY - start.y) > 8)) return;
          e.preventDefault();
          openDetail();
        }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 3px 12px rgba(180,100,140,0.13)"; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(180,100,140,0.06)"; }}
      >
        {/* Selection checkbox (select mode) OR task done-checkbox (normal mode).
            Events/reminders get neither in normal mode — they can't be marked done. */}
        {selectMode ? (
          <div style={{
            width: 22, height: 22, borderRadius: 7, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: selected ? "none" : "2px solid var(--c-border)",
            background: selected ? "var(--c-primary-deep)" : "transparent",
            color: "#fff", transition: "background 0.12s, border-color 0.12s",
          }}>
            {selected && <Check size={14} strokeWidth={3.5} />}
          </div>
        ) : asTask ? (
          <div data-no-detail onClick={e => e.stopPropagation()}>
            {/* Black, or white on dark backgrounds / dark mode, never the accent (Jade, 2026-10-24). */}
            <HabitCheckbox checked={isDone} color={ink.ink} tickColor={ink.dark ? "#111111" : "#ffffff"} onChange={handleTaskCheckboxChange} size={28} />
          </div>
        ) : null}

        {/* Events / reminders have no colour dot before the title (Jade, 2026-11-02). */}

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              flex: "1 1 auto", minWidth: 0,
              fontSize: 15, fontWeight: 700,
              textDecoration: isDone ? "line-through" : "none",
              color: (isDone || isPassed) ? "#b0a0b0" : "var(--c-text)",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {item.title || item.name || "Untitled"}
            </span>
            {type === "task" && <ListPriorityFlag priority={item.priority} />}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
            {/* Day/date dropped — the Pending/Finished (or Upcoming/Passed)
                sub-tabs and date-group headers above already separate cards
                by day, so repeating it per-card was redundant. Time (when
                the item has one) is still worth keeping here. */}
            {timeStr && (
              <span style={{
                fontSize: 10, color: "#b090a8",
                fontWeight: 600, display: "flex", alignItems: "center", gap: 3,
              }}>
                {timeStr}
              </span>
            )}
            {item.skipped && (
              <span style={{
                fontSize: 9, fontWeight: 800, color: "#7c4fd4",
                background: "var(--c-bg-soft)", borderRadius: 7, padding: "2px 6px",
                letterSpacing: "0.04em", flexShrink: 0,
              }}>📅 SKIPPED</span>
            )}
            <ListRepeatBadge item={item} />
          </div>
          {item.skipNote && (
            <div style={{ fontSize: 10, color: "#9070b0", marginTop: 3, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              💭 {item.skipNote}
            </div>
          )}
        </div>

        {/* "due Oct 12" / "due today" — only for a due date the person actually set
            (one that differs from the start date; see taskDueLabel). Same look as the
            counted habit's "due …" label in the Habits tab: plain italic accent text. */}
        {type === "task" && taskDueLabel(item, todayStr()) && (
          <span style={{ fontSize: 11, fontWeight: 600, fontStyle: "italic", color: "var(--c-text-accent)", whiteSpace: "nowrap", flexShrink: 0 }}>
            {taskDueLabel(item, todayStr())}
          </span>
        )}

        {/* Drag handle — desktop only, not in month/year view */}
        {isDesktop && !isDone && !isPassed && !selectMode && calSubTab !== "month" && calSubTab !== "year" && (
          <div data-no-detail title="Drag to week calendar" style={{
            flexShrink: 0, display: "flex", flexDirection: "column", gap: 3,
            padding: "0 4px", cursor: "grab", opacity: 0.35,
          }}>
            <div style={{ width: 14, height: 2, borderRadius: 1, background: "var(--c-text-soft)" }} />
            <div style={{ width: 14, height: 2, borderRadius: 1, background: "var(--c-text-soft)" }} />
            <div style={{ width: 14, height: 2, borderRadius: 1, background: "var(--c-text-soft)" }} />
          </div>
        )}

      </div>
    </div>
  );
}, (a, b) => (
  // Custom equality: the `x` wrapper is rebuilt every render, but `x.item` is the
  // real task/event object — and the app updates items immutably, so its identity
  // only changes when the item actually changes. Comparing on that (plus the few
  // primitive flags) lets every untouched card skip re-rendering, which is what
  // keeps list-view dragging smooth and unrelated clicks snappy on desktop.
  a.x.item === b.x.item &&
  a.x.type === b.x.type &&
  a.x.date === b.x.date &&
  a.isDesktop === b.isDesktop &&
  a.calSubTab === b.calSubTab &&
  a.categories === b.categories &&
  a.selectMode === b.selectMode &&
  a.selected === b.selected
  // onToggleSelect is intentionally excluded: it's a fresh closure each render but
  // only closes over `x` (compared above) and stable functional setters, so a card
  // never needs to re-render just because the parent handed it a new closure.
));

